// TasksKanban — трекер задач (OSS-TASK-3/4), канбан поверх backlog_items.
// Данные через POST /backlog/v1 (action-router). Канбан-колонки = статусы.
// DnD — @hello-pangea/dnd (MIT). LLM не используется. Гейт backlogEdit на мутации.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Form, Modal, Spinner } from 'react-bootstrap';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { v4 as uuidv4 } from 'uuid';
import ApiService from '../../helpers/apiService';
import { hasAccess } from '../../helpers/access';

interface TaskLabel { id: string; name: string; color: string; }
interface TaskComment { id: string; content: string; created_at: string; }
interface Task {
  id: string;
  title: string;
  analysis_md?: string;
  priority: string;
  status: string;
  seq_id?: number;
  assignee_id?: string;
  parent_id?: string;
  sort_order?: number;
  labels?: TaskLabel[];
  comments?: TaskComment[];
}

const COLUMNS = [
  { key: 'proposed', label: 'Предложена' },
  { key: 'approved', label: 'Одобрена' },
  { key: 'in_progress', label: 'В работе' },
  { key: 'waiting', label: 'Ожидание' },
  { key: 'done', label: 'Готово' },
  { key: 'rejected', label: 'Отклонена' },
];
const PRIORITIES = [
  { key: 'critical', label: 'Критич.', bg: 'danger' },
  { key: 'high', label: 'Высокий', bg: 'warning' },
  { key: 'medium', label: 'Средний', bg: 'info' },
  { key: 'low', label: 'Низкий', bg: 'secondary' },
];
const prMeta = (k: string) => PRIORITIES.find((p) => p.key === k) || { key: k, label: k, bg: 'secondary' };

const TasksKanban: React.FC = () => {
  const canEdit = hasAccess('backlogEdit');
  const [tasks, setTasks] = useState<Task[]>([]);
  const [labels, setLabels] = useState<TaskLabel[]>([]);
  const [operators, setOperators] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<Task | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newPriority, setNewPriority] = useState('medium');

  const reqId = useMemo(() => uuidv4(), []);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [items, lbls] = await Promise.all([
        ApiService.backlogAction('list'),
        ApiService.backlogAction('label_list'),
      ]);
      setTasks(Array.isArray(items) ? items : []);
      setLabels(Array.isArray(lbls) ? lbls : []);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Ошибка загрузки задач');
      setTasks([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    // Имена исполнителей (операторов) для карточек.
    ApiService.getOperators<any>(reqId)
      .then((r) => {
        const map: Record<string, string> = {};
        (r?.data || []).forEach((o: any) => { if (o?.id) map[o.id] = o.name || o.id; });
        setOperators(map);
      })
      .catch(() => setOperators({}));
  }, [reqId]);

  // Задачи по колонкам (только верхнего уровня — без подзадач), отсортированы по sort_order.
  const byColumn = useMemo(() => {
    const m: Record<string, Task[]> = {};
    COLUMNS.forEach((c) => { m[c.key] = []; });
    tasks.filter((t) => !t.parent_id).forEach((t) => { (m[t.status] = m[t.status] || []).push(t); });
    Object.values(m).forEach((arr) => arr.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
    return m;
  }, [tasks]);

  const onDragEnd = useCallback(async (res: DropResult) => {
    if (!canEdit || !res.destination) return;
    const { draggableId, destination } = res;
    const destStatus = destination.droppableId;
    const destList = byColumn[destStatus] || [];
    // sort_order = середина между соседями в целевой колонке.
    const above = destList[destination.index - 1]?.sort_order;
    const below = destList[destination.index]?.sort_order;
    let sortOrder: number;
    if (above != null && below != null) sortOrder = (above + below) / 2;
    else if (above != null) sortOrder = above + 1000;
    else if (below != null) sortOrder = below - 1000;
    else sortOrder = Date.now() / 1000;
    // Оптимистично.
    setTasks((prev) => prev.map((t) => (t.id === draggableId ? { ...t, status: destStatus, sort_order: sortOrder } : t)));
    try {
      await ApiService.backlogAction('move', { id: draggableId, status: destStatus, sort_order: sortOrder });
    } catch {
      load(); // откат через перезагрузку
    }
  }, [canEdit, byColumn, load]);

  const openDetail = useCallback(async (id: string) => {
    try { setDetail(await ApiService.backlogAction('get', { id })); }
    catch { /* ignore */ }
  }, []);

  const createTask = useCallback(async () => {
    if (!newTitle.trim()) return;
    await ApiService.backlogAction('create', { title: newTitle.trim(), priority: newPriority, status: 'proposed', source: 'manual' });
    setShowNew(false); setNewTitle(''); setNewPriority('medium');
    load();
  }, [newTitle, newPriority, load]);

  if (loading && !tasks.length) {
    return <div className="d-flex align-items-center gap-2 p-3"><Spinner animation="border" size="sm" /><span>Загрузка…</span></div>;
  }

  return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <Card.Title className="mb-0">Задачи (канбан)</Card.Title>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" size="sm" onClick={load} disabled={loading}>Обновить</Button>
          {canEdit && <Button variant="primary" size="sm" onClick={() => setShowNew(true)}>+ Задача</Button>}
        </div>
      </Card.Header>
      <Card.Body style={{ overflowX: 'auto' }}>
        {error && <div className="alert alert-danger py-2 small">{error}</div>}
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="d-flex gap-2" style={{ minWidth: 'min-content' }}>
            {COLUMNS.map((col) => (
              <Droppable droppableId={col.key} key={col.key}>
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    style={{ width: 230, minWidth: 230, background: '#F8F9FA', borderRadius: 8, padding: 8 }}
                  >
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="fw-semibold small">{col.label}</span>
                      <Badge bg="secondary" style={{ fontSize: 10 }}>{byColumn[col.key]?.length || 0}</Badge>
                    </div>
                    {(byColumn[col.key] || []).map((t, idx) => (
                      <Draggable draggableId={t.id} index={idx} key={t.id} isDragDisabled={!canEdit}>
                        {(prov) => (
                          <div
                            ref={prov.innerRef}
                            {...prov.draggableProps}
                            {...prov.dragHandleProps}
                            onClick={() => openDetail(t.id)}
                            style={{ background: '#fff', border: '1px solid #E0E0E0', borderRadius: 6, padding: '6px 8px', marginBottom: 6, cursor: 'pointer', ...prov.draggableProps.style }}
                          >
                            <div className="d-flex justify-content-between align-items-start">
                              <span className="text-muted" style={{ fontSize: 10 }}>{t.seq_id ? `#${t.seq_id}` : ''}</span>
                              <Badge bg={prMeta(t.priority).bg} style={{ fontSize: 9 }}>{prMeta(t.priority).label}</Badge>
                            </div>
                            <div style={{ fontSize: 12, marginTop: 2 }}>{t.title}</div>
                            <div className="d-flex flex-wrap gap-1 mt-1">
                              {(t.labels || []).map((l) => (
                                <span key={l.id} style={{ fontSize: 9, background: l.color, color: '#fff', borderRadius: 4, padding: '0 5px' }}>{l.name}</span>
                              ))}
                            </div>
                            {t.assignee_id && (
                              <div className="text-muted mt-1" style={{ fontSize: 10 }}>👤 {operators[t.assignee_id] || '—'}</div>
                            )}
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            ))}
          </div>
        </DragDropContext>
      </Card.Body>

      {/* Новая задача */}
      <Modal show={showNew} onHide={() => setShowNew(false)}>
        <Modal.Header closeButton><Modal.Title>Новая задача</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-2">
            <Form.Label className="small">Название</Form.Label>
            <Form.Control size="sm" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} autoFocus />
          </Form.Group>
          <Form.Group>
            <Form.Label className="small">Приоритет</Form.Label>
            <Form.Select size="sm" value={newPriority} onChange={(e) => setNewPriority(e.target.value)}>
              {PRIORITIES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" onClick={() => setShowNew(false)}>Отмена</Button>
          <Button variant="primary" size="sm" onClick={createTask} disabled={!newTitle.trim()}>Создать</Button>
        </Modal.Footer>
      </Modal>

      {detail && (
        <TaskDetailModal
          task={detail}
          labels={labels}
          operators={operators}
          canEdit={canEdit}
          allTasks={tasks}
          onClose={() => setDetail(null)}
          onChanged={() => { load(); openDetail(detail.id); }}
        />
      )}
    </Card>
  );
};

// ── Модал задачи (TASK-4): исполнитель, метки, подзадачи, комментарии ──
const TaskDetailModal: React.FC<{
  task: Task; labels: TaskLabel[]; operators: Record<string, string>; canEdit: boolean;
  allTasks: Task[]; onClose: () => void; onChanged: () => void;
}> = ({ task, labels, operators, canEdit, allTasks, onClose, onChanged }) => {
  const [comment, setComment] = useState('');
  const [subTitle, setSubTitle] = useState('');
  const subtasks = allTasks.filter((t) => t.parent_id === task.id);
  const taskLabelIds = new Set((task.labels || []).map((l) => l.id));

  const act = async (action: string, payload: Record<string, any>) => {
    try { await ApiService.backlogAction(action, payload); onChanged(); } catch { /* ignore */ }
  };

  return (
    <Modal show onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>{task.seq_id ? `#${task.seq_id} · ` : ''}{task.title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row g-3">
          <div className="col-md-6">
            <Form.Label className="small mb-1">Исполнитель</Form.Label>
            <Form.Select size="sm" value={task.assignee_id || ''} disabled={!canEdit}
              onChange={(e) => act('set_assignee', { id: task.id, assignee_id: e.target.value })}>
              <option value="">— не назначен —</option>
              {Object.entries(operators).map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </Form.Select>
          </div>
          <div className="col-md-6">
            <Form.Label className="small mb-1">Статус</Form.Label>
            <Form.Select size="sm" value={task.status} disabled={!canEdit}
              onChange={(e) => act('move', { id: task.id, status: e.target.value, sort_order: task.sort_order || 0 })}>
              {COLUMNS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </Form.Select>
          </div>
          <div className="col-12">
            <Form.Label className="small mb-1">Метки</Form.Label>
            <div className="d-flex flex-wrap gap-1">
              {labels.map((l) => {
                const on = taskLabelIds.has(l.id);
                return (
                  <span key={l.id}
                    onClick={() => canEdit && act(on ? 'label_unlink' : 'label_link', { id: task.id, label_id: l.id })}
                    style={{ fontSize: 11, background: on ? l.color : '#eee', color: on ? '#fff' : '#666', borderRadius: 4, padding: '2px 8px', cursor: canEdit ? 'pointer' : 'default', opacity: on ? 1 : 0.7 }}>
                    {l.name}
                  </span>
                );
              })}
              {labels.length === 0 && <span className="text-muted small">Меток нет</span>}
            </div>
          </div>

          <div className="col-12">
            <Form.Label className="small mb-1">Подзадачи ({subtasks.length})</Form.Label>
            {subtasks.map((s) => (
              <div key={s.id} className="d-flex justify-content-between small border-top py-1">
                <span>{s.seq_id ? `#${s.seq_id} ` : ''}{s.title}</span>
                <Badge bg="secondary" style={{ fontSize: 9 }}>{COLUMNS.find((c) => c.key === s.status)?.label || s.status}</Badge>
              </div>
            ))}
            {canEdit && (
              <div className="d-flex gap-2 mt-2">
                <Form.Control size="sm" placeholder="Новая подзадача" value={subTitle} onChange={(e) => setSubTitle(e.target.value)} />
                <Button size="sm" variant="outline-primary" disabled={!subTitle.trim()}
                  onClick={() => { act('create', { title: subTitle.trim(), parent_id: task.id, status: 'proposed', source: 'manual' }); setSubTitle(''); }}>
                  +
                </Button>
              </div>
            )}
          </div>

          <div className="col-12">
            <Form.Label className="small mb-1">Комментарии</Form.Label>
            {(task.comments || []).map((c) => (
              <div key={c.id} className="border-top py-1 small">{c.content}</div>
            ))}
            {canEdit && (
              <div className="d-flex gap-2 mt-2">
                <Form.Control size="sm" placeholder="Комментарий" value={comment} onChange={(e) => setComment(e.target.value)} />
                <Button size="sm" variant="outline-secondary" disabled={!comment.trim()}
                  onClick={() => { act('comment', { id: task.id, content: comment.trim() }); setComment(''); }}>
                  Добавить
                </Button>
              </div>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        {canEdit && (
          <Button variant="outline-danger" size="sm" className="me-auto"
            onClick={() => { if (window.confirm('Удалить задачу?')) { act('delete', { id: task.id }); onClose(); } }}>
            Удалить
          </Button>
        )}
        <Button variant="secondary" size="sm" onClick={onClose}>Закрыть</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TasksKanban;
